import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Avatar,
  Box,
  Grid,
  Spinner,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";

/**
 * Generic selector modal for BAA-provided entities:
 * - entityType: 'physician' | 'facility' | 'patient'
 * - baaData: preloaded BAA object (from getBaaDataForUser)
 * - onSelect: called with the selected entity object
 */
const BaaEntitySelectorModal = ({
  isOpen,
  onClose,
  entityType = "physician",
  baaData = {},
  physicians: physiciansProp,
  facilities: facilitiesProp,
  patients: patientsProp,
  selectedEntity = null,
  initialLoading = false,
}) => {
  const safeParse = (v, fallback = {}) => {
    if (!v) return fallback;
    if (typeof v === "object") return v;
    if (typeof v === "string") {
      try {
        return JSON.parse(v);
      } catch {
        return fallback;
      }
    }
    return fallback;
  };
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(initialLoading);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    try {
      // If an external list was provided, use it
      if (entityType === "physician" && Array.isArray(physiciansProp)) {
        setItems(physiciansProp);
        setLoading(false);
        return;
      }
      if (entityType === "facility" && Array.isArray(facilitiesProp)) {
        setItems(facilitiesProp);
        setLoading(false);
        return;
      }
      if (entityType === "patient" && Array.isArray(patientsProp)) {
        setItems(patientsProp);
        setLoading(false);
        return;
      }

      // Derive items from baaData when available
      if (baaData) {
        if (entityType === "physician") {
          const main = baaData?.physicianInfo ? [{ ...baaData.physicianInfo, isPrimary: true }] : [];
          const additional = Array.isArray(baaData?.additionalPhysicians)
            ? baaData.additionalPhysicians.map((p) => ({ ...p, isPrimary: false }))
            : (() => {
                try {
                  return JSON.parse(baaData?.additionalPhysicians || "[]").map((p) => ({ ...p, isPrimary: false }));
                } catch { return []; }
              })();

          setItems([...main, ...additional]);
        } else if (entityType === "facility") {
            // facilityAddress may be an object or stringified JSON
            // also accept top-level facilityName/Phone/NPI/TIN as fallbacks
            const rawFac = baaData?.facilityAddress || baaData?.facility || null;
            const fac = rawFac;
          let list = [];
          if (fac) {
            if (Array.isArray(fac)) list = fac;
            else if (typeof fac === "string") {
              try { list = [JSON.parse(fac)]; } catch { list = [{ name: fac }]; }
            } else list = [fac];
          }
            // Apply fallbacks from baaData root for entries missing name/phone/etc
            const fallbackName = baaData?.facilityName || baaData?.facility?.name || baaData?.facility || "";
            const fallbackPhone = baaData?.facilityPhone || baaData?.facilityPhone || baaData?.facility?.phone || "";
            const fallbackFax = baaData?.facilityFax || baaData?.facility?.fax || "";
            const fallbackNpi = baaData?.facilityNPI || baaData?.facility?.npi || "";
            const fallbackTin = baaData?.facilityTIN || baaData?.facility?.tin || "";
            const fallbackPtan = baaData?.facilityPTAN || baaData?.facility?.ptan || "";

            const normalized = list.map((entry) => {
              const e = safeParse(entry, entry || {});
              return {
                name: e.name || e.facilityName || e.facility || fallbackName || "",
                phone: (e.phone || e.facilityPhone || e.facility_phone || fallbackPhone || "") + "",
                fax: (e.fax || e.facilityFax || fallbackFax || "") + "",
                npi: (e.npi || e.facilityNPI || fallbackNpi || "") + "",
                tin: (e.tin || e.facilityTIN || fallbackTin || "") + "",
                ptan: (e.ptan || e.facilityPTAN || fallbackPtan || "") + "",
                street: e.street || e.address || e.address_line1 || "",
                city: e.city || "",
                state: e.state || "",
                zip: e.zip != null ? `${e.zip}` : (e.postalCode != null ? `${e.postalCode}` : ""),
                ...e,
              };
            });

            setItems(normalized);
        } else if (entityType === "patient") {
          // Try patient fields or shippingAddresses
          const patient = baaData?.patient || null;
          const addresses = Array.isArray(baaData?.shippingAddresses) ? baaData.shippingAddresses : (() => {
            try { return JSON.parse(baaData?.shippingAddresses || "[]"); } catch { return []; }
          })();
          const list = [];
          if (patient) list.push(patient);
          if (addresses.length) list.push(...addresses);
          setItems(list);
        }
      }
    } catch (err) {
      console.error("Error preparing BAA items:", err);
      toast({ title: "Error", description: "Failed to load items.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, baaData, physiciansProp, facilitiesProp, patientsProp, entityType]);

  const filtered = useMemo(() => {
    if (!searchTerm) return items;
    const s = searchTerm.toLowerCase();
    return items.filter((it) => {
      if (!it) return false;
      if (entityType === "physician") {
        const first = (it.physicianFirstName || it.firstName || "").toLowerCase();
        const last = (it.physicianLastName || it.lastName || "").toLowerCase();
        const full = `${first} ${last}`.trim();
        return full.includes(s) || (it.specialty || "").toLowerCase().includes(s) || (it.npi || "").toLowerCase().includes(s);
      }
      if (entityType === "facility") {
        const name = (it.name || it.facilityName || "").toLowerCase();
        const phone = (it.phone || "").toLowerCase();
        const addr = [it.street, it.city, it.state, it.zip].filter(Boolean).join(" ").toLowerCase();
        return name.includes(s) || phone.includes(s) || addr.includes(s);
      }
      // patient
      const pname = (it.name || it.patientName || "").toLowerCase();
      const pphone = (it.phone || "").toLowerCase();
      return pname.includes(s) || pphone.includes(s) || (it.dob || "").toLowerCase().includes(s);
    });
  }, [items, searchTerm, entityType]);

  const getTitle = () => {
    if (entityType === "physician") return "Select Physician";
    if (entityType === "facility") return "Select Facility";
    return "Select Patient";
  };

  const getItemLabel = (it) => {
    if (!it) return "";
    if (entityType === "physician") {
      const first = it.physicianFirstName || it.firstName || "";
      const last = it.physicianLastName || it.lastName || "";
      return `${first} ${last}`.trim() || it.npi || "Unknown Physician";
    }
    if (entityType === "facility") return it.name || it.facilityName || "Unknown Facility";
    return it.name || it.patientName || it.addressLine1 || "Unknown";
  };

  const handleSelect = (it) => {
    // pass selected object back
    if (typeof it === "undefined") return;
    if (it) {
      // simple close and callback pattern
      if (typeof (BaaEntitySelectorModal.onSelect) === "function") {
        // noop
      }
    }
    // expose selection via custom event: we'll call the provided onSelect prop via the parent by passing onSelect in props
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{getTitle()}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <InputGroup>
              <InputLeftElement pointerEvents="none"><SearchIcon color="gray.300" /></InputLeftElement>
              <Input
                placeholder={entityType === "physician" ? "Search by name, specialty, or NPI..." : "Search..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="white"
                pl="2.25rem"
              />
            </InputGroup>

            {loading ? (
              <Box textAlign="center" py={8}>
                <Spinner size="lg" />
                <Text mt={2} color="gray.600">Loading...</Text>
              </Box>
            ) : (
              <Box maxH="400px" overflowY="auto">
                <Grid templateColumns="1fr" gap={3}>
                  {filtered.map((p, idx) => {
                    const label = getItemLabel(p);
                    const isSelected = selectedEntity && JSON.stringify(selectedEntity) === JSON.stringify(p);
                    return (
                      <Box
                        key={idx}
                        p={4}
                        borderWidth="2px"
                        borderRadius="lg"
                        cursor="pointer"
                        borderColor={isSelected ? "teal.500" : "gray.200"}
                        bg={isSelected ? "teal.50" : "white"}
                        _hover={{ borderColor: "teal.300", bg: "teal.25" }}
                        onClick={() => { if (typeof p !== 'undefined') { (BaaEntitySelectorModal.onSelectProp || (()=>{}))(p); onClose(); } }}
                      >
                        <HStack spacing={3} align="start">
                          <Avatar name={label} size="md" />
                          <VStack align="start" spacing={1} flex="1">
                            <HStack>
                              <Text fontWeight="bold" fontSize="md">{label}</Text>
                              {entityType === "physician" && p.isPrimary && (
                                <Text fontSize="xs" color="blue.600" fontWeight="semibold">PRIMARY</Text>
                              )}
                            </HStack>

                            {entityType === "physician" && p.specialty && (
                              <Text fontSize="sm" color="gray.600"><strong>Specialty:</strong> {p.specialty}</Text>
                            )}
                            {entityType === "physician" && p.npi && (
                              <Text fontSize="sm" color="gray.600"><strong>NPI:</strong> {p.npi}</Text>
                            )}

                            {(entityType === "facility") && (p.phone || p.fax) && (
                              <HStack spacing={4}>
                                {p.phone && <Text fontSize="sm" color="gray.600"><strong>Phone:</strong> {p.phone}</Text>}
                                {p.fax && <Text fontSize="sm" color="gray.600"><strong>Fax:</strong> {p.fax}</Text>}
                              </HStack>
                            )}

                            {(p.street || p.city || p.state) && (
                              <Text fontSize="sm" color="gray.600">
                                <strong>Address:</strong> {[p.street, p.city, p.state, p.zip].filter(Boolean).join(", ")}
                              </Text>
                            )}
                          </VStack>
                        </HStack>
                      </Box>
                    );
                  })}
                </Grid>

                {filtered.length === 0 && !loading && (
                  <Box textAlign="center" py={8}>
                    <Text color="gray.500">{searchTerm ? "No items match your search." : "No items available."}</Text>
                  </Box>
                )}
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Adapter: allow parent to inject onSelect handler through a static prop used above when clicking an item.
// This is a small pragmatic pattern to avoid recreating many closures inside the list rendering.
BaaEntitySelectorModal.setOnSelect = (fn) => { BaaEntitySelectorModal.onSelectProp = fn; };

export default BaaEntitySelectorModal;
